<template>
    <div>
        <div class="container max-w-3xl mx-auto">
            <div class="pt-4 pb-16 xl:pt-8 md:pb-20 xl:pb-24">
                <div class="md:text-center">
                    <h1 class="max-w-lg mx-auto mb-4 text-3xl font-semibold leading-tight tracking-tight font-noto-sans xs:text-4xl md:text-5xl lg:text-6xl text-grey-darkest">
                        We're making renting<br>
                        <span class="text-blue">better for landlords &amp; agents</span>
                    </h1>

                    <h2 class="max-w-md mx-auto mb-6 lg:mb-12 md:text-xl xl:text-2xl">
                        Market properties, collect rent, perform inventories, store important documents, stay organised and more.
                    </h2>
                </div>

                <div class="max-w-sm mx-auto">
                    <div class="flex flex-col justify-center mb-4 md:flex-row">
                        <laroute-link
                            :to="{
                                name: 'auth.users.create'
                            }"
                            class="flex-auto mb-3 shadow-md md:mb-0 md:mr-3 btn btn-blue btn-xl"
                        >
                            Get started
                        </laroute-link>

                        <callback-requests-create-button
                            class="flex-auto shadow-md btn btn-white btn-xl"
                        >
                            Request a callback
                        </callback-requests-create-button>
                    </div>

                    <router-link
                        :to="{ name: 'www.tenants.index' }"
                        class="flex items-center font-semibold text-blue"
                    >
                        Are you a tenant?

                        <base-svg
                            name="icon-cheveron-right"
                            class="flex-none w-6 h-6 fill-primary-blue"
                        />
                    </router-link>
                </div>
            </div>

            <grid-layout class="md:pb-6 lg:pb-16 xl:pb-20">
                <grid-layout-item class="mb-12 lg:mb-0 lg:w-1/3">
                    <div class="inline-block p-3 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                        <base-svg
                            name="icon-tune"
                            class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                        />
                    </div>

                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                        All-in-one solution
                    </h6>

                    <p>
                        List your property, review applications, manage tenancies, perform inventories, share documents, get paid and more - anywhere, on any device, 24/7.
                    </p>
                </grid-layout-item>

                <grid-layout-item class="mb-12 lg:mb-0 lg:w-1/3">
                    <div class="inline-block p-3 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                        <base-svg
                            name="icon-money"
                            class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                        />
                    </div>

                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                        Direct payments
                    </h6>

                    <p>
                        You get paid directly (and quickly). Payments from tenants are processed and deposited directly into your nominated bank account.
                    </p>
                </grid-layout-item>

                <grid-layout-item class="mb-12 lg:mb-0 lg:w-1/3">
                    <div class="inline-block p-3 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                        <base-svg
                            name="icon-user-group"
                            class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                        />
                    </div>

                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                        Landlord and agent friendly
                    </h6>

                    <p>
                        Built to scale, Rentora is usable by everyone. From single property private landlords, to nationwide estate agents.
                    </p>
                </grid-layout-item>
            </grid-layout>
        </div>

        <svg
            class="w-full h-10 text-blue-bg"
            preserveAspectRatio="none"
            viewBox="0 0 375 40"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M375 40V0s-87.046 32-187.5 32S0 0 0 0v40h375z" />
        </svg>

        <div class="bg-blue-bg">
            <div class="container max-w-3xl pt-16 mx-auto xl:pt-32">
                <grid-layout class="flex-col-reverse items-center lg:flex-row">
                    <grid-layout-item class="lg:w-1/2">
                        <h3 class="mb-4 text-2xl font-semibold leading-tight tracking-tight xs:text-3xl font-noto-sans">
                            <router-link
                                :to="{
                                    name: 'www.landlords.listings.index'
                                }"
                                class="flex items-center text-blue"
                            >
                                Property listings

                                <base-svg
                                    name="icon-cheveron-right"
                                    class="w-8 h-8 fill-primary-blue"
                                />
                            </router-link>
                        </h3>

                        <p class="mb-12 text-lg lg:text-xl">
                            Avoid costly vacancies. Publicly list your properties, and/or invite tenants to apply with a unique link. Every Rentora listing has a built-in rental application.
                        </p>

                        <div class="flex flex-col items-start mb-12 md:flex-row">
                            <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                <base-svg
                                    name="icon-home"
                                    class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                                />
                            </div>

                            <div class="flex-grow md:pl-6">
                                <div class="mb-1 text-sm uppercase text-grey-dark">
                                    Listings
                                </div>

                                <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                    Market your property
                                    <span class="ml-2 text-sm italic font-normal text-blue">included</span>
                                </h6>

                                List your properties - <strong>for free</strong> - on our website.
                                <router-link
                                    :to="{
                                        name: 'listings.index'
                                    }"
                                    class="text-blue"
                                >
                                    Our property search
                                </router-link>
                                is wicked fast and built for a world where social media dominates.
                            </div>
                        </div>

                        <div class="flex flex-col items-start md:flex-row">
                            <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                <base-svg
                                    name="icon-inbox"
                                    class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                                />
                            </div>

                            <div class="flex-grow md:pl-6">
                                <div class="mb-1 text-sm uppercase text-grey-dark">
                                    Applications
                                </div>

                                <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                    Choose the right tenants
                                    <span class="ml-2 text-sm italic font-normal text-blue">included</span>
                                </h6>

                                <p class="mb-8">
                                    Prospective tenants submit applications via the built-in application. Browse and shortlist them at your leisure - reach out to the ones that fit.
                                </p>

                                <router-link
                                    :to="{ name: 'www.landlords.listings.index' }"
                                    class="flex items-center text-blue"
                                >
                                    See more property listing features

                                    <base-svg
                                        name="icon-cheveron-right"
                                        class="w-6 h-6 fill-primary-blue"
                                    />
                                </router-link>
                            </div>
                        </div>
                    </grid-layout-item>
                    <grid-layout-item class="mb-6 lg:mb-0 lg:w-1/2">
                        <img
                            src="/img/property-listing.svg"
                            class="w-full mx-auto"
                            :style="{
                                'max-width': '410px'
                            }"
                        >
                    </grid-layout-item>
                </grid-layout>
            </div>

            <div
                class="pt-24 bg-center bg-no-repeat lg:pt-48"
                :style="{
                    'background-image': 'url(/img/misc-background.svg)',
                    'background-position': 'top 50px left -50px',
                    'background-size': '462px 600px'
                }"
            >
                <div class="container max-w-3xl mx-auto">
                    <grid-layout class="flex-col-reverse items-center lg:flex-row-reverse">
                        <grid-layout-item class="lg:w-1/2">
                            <h3 class="mb-4 text-2xl font-semibold leading-tight tracking-tight xs:text-3xl font-noto-sans">
                                <router-link
                                    :to="{ name: 'www.landlords.rent-collection.index' }"
                                    class="flex items-center text-blue"
                                >
                                    Rent Collection

                                    <base-svg
                                        name="icon-cheveron-right"
                                        class="w-8 h-8 fill-primary-blue"
                                    />
                                </router-link>
                            </h3>

                            <p class="mb-12 text-lg lg:text-xl">
                                We issue reminders, collect and check money in against money due and deposit it into your bank account. You don't have to do anything.
                            </p>

                            <div class="flex flex-col items-start mb-12 md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-money"
                                        class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Direct payments
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Never worry about rent
                                        <span class="ml-2 text-sm italic font-normal text-blue">included</span>
                                    </h6>

                                    <p class="mb-2">
                                        Receive payment for rent, and other bills, quickly and <strong>directly</strong> into your bank account.
                                    </p>

                                    <p>
                                        <strong>We never touch your money.</strong>
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col items-start mb-12 md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-card"
                                        class="w-8 h-8 fill-primary-blue-lighter fill-secondary-blue"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Online card payments
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Payments with a click
                                        <span class="ml-2 text-sm italic font-normal text-blue">included</span>
                                    </h6>

                                    <p>
                                        Tenants can use their credit and debit cards to make payments towards their rent at
                                        any time. They can even opt to split the payment across multiple cards, or with roommates.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col items-start md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-mood-happy"
                                        class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Budgeting
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Happy tenants pay on time
                                        <span class="ml-2 text-sm italic font-normal text-blue">included</span>
                                    </h6>

                                    <p class="mb-3">
                                        Some people get paid weekly, some are self employed and others pick up whatever jobs they can to pay the bills.
                                        Give your tenants the flexibility to budget and pay their rent on a schedule that suits them.
                                    </p>

                                    <p class="mb-8">
                                        We keep track of the payments. You don't have to do anything.
                                    </p>

                                    <router-link
                                        :to="{ name: 'www.landlords.rent-collection.index' }"
                                        class="flex items-center text-blue"
                                    >
                                        See more rent collection features

                                        <base-svg
                                            name="icon-cheveron-right"
                                            class="w-6 h-6 fill-primary-blue"
                                        />
                                    </router-link>
                                </div>
                            </div>
                        </grid-layout-item>
                        <grid-layout-item class="mb-6 lg:mb-0 lg:w-1/2">
                            <img
                                src="/img/rent-collection.svg"
                                class="w-full mx-auto"
                                :style="{
                                    'max-width': '439px'
                                }"
                            >
                        </grid-layout-item>
                    </grid-layout>
                </div>
            </div>

            <div
                class="py-24 bg-center bg-no-repeat lg:py-48"
                :style="{
                    'background-image': 'url(/img/misc-background.svg)',
                    'background-position': 'top 75px right 50px',
                    'background-size': '462px 600px'
                }"
            >
                <div class="container max-w-3xl mx-auto">
                    <grid-layout class="flex-col-reverse items-center lg:flex-row">
                        <grid-layout-item class="lg:w-1/2">
                            <h3 class="mb-4 text-2xl font-semibold leading-tight tracking-tight xs:text-3xl font-noto-sans">
                                <router-link
                                    :to="{ name: 'www.landlords.organisation.index' }"
                                    class="flex items-center text-blue"
                                >
                                    Stay organised

                                    <base-svg
                                        name="icon-cheveron-right"
                                        class="w-8 h-8 fill-primary-blue"
                                    />
                                </router-link>
                            </h3>

                            <p class="mb-12 text-lg lg:text-xl">
                                Centralized, all-in-one property management: shared inventories, document storage, calendar, notifications, messaging and more.
                            </p>

                            <div class="flex flex-col items-start mb-12 md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-camera"
                                        class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Inventory
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Quick, easy and shared inventories
                                        <span class="ml-2 text-sm italic font-normal text-blue">included</span>
                                    </h6>

                                    <p>
                                        With Rentora, create and store your inventories with <strong>unlimited</strong> photos and text from any device. We’ll keep them safe and share them with your tenants.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col items-start mb-12 md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-calendar-date"
                                        class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Calendar
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Stay legal!
                                        <span class="ml-2 text-sm italic font-normal text-blue">included</span>
                                    </h6>

                                    <p>
                                        We maintain a calendar for you (and your tenants), where you can see upcoming rents, tenancy terms, expiring documents and a lot more.
                                    </p>
                                </div>
                            </div>

                            <div class="flex flex-col items-start md:flex-row">
                                <div class="flex-none p-2 mb-4 rounded-full md:mb-0 border-3 border-blue-lighter">
                                    <base-svg
                                        name="icon-notification"
                                        class="w-8 h-8 fill-primary-blue fill-secondary-blue-lighter"
                                    />
                                </div>

                                <div class="flex-grow md:pl-6">
                                    <div class="mb-1 text-sm uppercase text-grey-dark">
                                        Notifications
                                    </div>

                                    <h6 class="mb-2 text-lg font-semibold font-noto-sans text-grey-darkest">
                                        Never miss a thing
                                        <span class="ml-2 text-sm italic font-normal text-blue">included</span>
                                    </h6>

                                    <p class="mb-8">
                                        You’ll be kept in the loop with everything via email and text - as will your tenants.
                                    </p>

                                    <router-link
                                        :to="{ name: 'www.landlords.organisation.index' }"
                                        class="flex items-center text-blue"
                                    >
                                        See more organisational features

                                        <base-svg
                                            name="icon-cheveron-right"
                                            class="w-6 h-6 fill-primary-blue"
                                        />
                                    </router-link>
                                </div>
                            </div>
                        </grid-layout-item>
                        <grid-layout-item class="mb-6 lg:mb-0 lg:w-1/2">
                            <img
                                src="/img/notifications.svg"
                                class="w-full mx-auto"
                                :style="{
                                    'max-width': '454px'
                                }"
                            >
                        </grid-layout-item>
                    </grid-layout>
                </div>
            </div>
        </div>

        <div class="container max-w-3xl mx-auto">
            <div class="pt-24 pb-12">
                <grid-layout>
                    <grid-layout-item class="lg:w-1/2">
                        <div class="flex mb-12">
                            <base-svg
                                name="icon-help-circle"
                                class="flex-none w-6 h-6 mr-6 fill-primary-green fill-secondary-green-lighter"
                            />

                            <div class="flex-grow">
                                <p class="mb-2 font-semibold font-noto-sans font-grey-darkest">
                                    Who are you?
                                </p>

                                <p>
                                    We're a team dedicated to reducing your costs and workload.
                                </p>
                            </div>
                        </div>

                        <div class="flex mb-12">
                            <base-svg
                                name="icon-help-circle"
                                class="flex-none w-6 h-6 mr-6 fill-primary-green fill-secondary-green-lighter"
                            />

                            <div class="flex-grow">
                                <p class="mb-2 font-semibold font-noto-sans font-grey-darkest">
                                    How much does it cost?
                                </p>

                                <p class="mb-3">
                                    £15/mo, no property limit, no storage limit, no minimum term - everything is included.
                                </p>

                                <p class="mb-3">
                                    See the
                                    <router-link
                                        :to="{
                                            name: 'www.pricing.index'
                                        }"
                                        class="text-blue"
                                    >
                                        pricing page
                                    </router-link>
                                    for details and more FAQs specifically for pricing.
                                </p>
                            </div>
                        </div>
                    </grid-layout-item>

                    <grid-layout-item class="lg:w-1/2">
                        <div class="flex mb-12">
                            <base-svg
                                name="icon-help-circle"
                                class="flex-none w-6 h-6 mr-6 fill-primary-green fill-secondary-green-lighter"
                            />

                            <div class="flex-grow">
                                <p class="mb-2 font-semibold font-noto-sans font-grey-darkest">
                                    I own an estate agency, can I/we use Rentora?
                                </p>

                                <p class="mb-3">
                                    Yes!
                                </p>

                                <p>
                                    If you'd like some help transitioning to Rentora, or setting up your account, please
                                    <router-link
                                        :to="{ name: 'www.contact.index' }"
                                        class="text-blue"
                                    >
                                        get in touch.
                                    </router-link>
                                </p>
                            </div>
                        </div>
                    </grid-layout-item>
                </grid-layout>
            </div>

            <div class="flex flex-col items-center max-w-xl p-3 mx-auto mb-24 rounded lg:flex-row bg-blue-lightest xs:p-6 lg:p-12">
                <div class="mb-6 lg:mb-0 lg:w-1/2">
                    <h6 class="text-2xl font-semibold md:text-3xl font-noto-sans text-blue-darkest">
                        Ready to get started?
                    </h6>

                    <p class="text-lg lg:text-xl">
                        Create an account in a couple of minutes
                    </p>
                </div>

                <div class="flex flex-col justify-center w-full lg:w-1/2 md:flex-row lg:justify-end">
                    <laroute-link
                        :to="{
                            name: 'auth.users.create'
                        }"
                        class="w-full mb-3 shadow-md md:mb-0 md:mr-3 md:w-auto btn btn-blue-darkest btn-xl"
                    >
                        Get started
                    </laroute-link>

                    <callback-requests-create-button
                        class="w-full shadow-md md:w-auto btn btn-white btn-xl"
                    >
                        Request a callback
                    </callback-requests-create-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import HeadMixin from '../../../mixins/head'

    export default {
        mixins: [
            HeadMixin
        ],
        mounted () {
            if (this.$route.hash) {
                setTimeout(() => {
                    location.href = this.$route.hash
                }, 1)
            }
        },
        methods: {
            head () {
                return {
                    title: [
                        'Rentora for landlords & agents',
                        'List properties, collect rent, perform inventories and more'
                    ]
                }
            }
        }
    }
</script>
